// « En ligne » — et, quand on ne l'est pas, depuis quand.
//
// L'application savait déjà qui était présent. Elle ne savait pas dire depuis
// COMBIEN DE TEMPS quelqu'un ne l'était plus : un membre hors ligne depuis
// trois minutes et un membre parti depuis trois semaines se ressemblaient.
// C'est pourtant ce qui décide si l'on écrit maintenant ou si l'on attend.
//
// Une seule brique, utilisée dans le profil, l'entête d'une conversation et la
// liste des conversations — pour que la même personne ne soit jamais décrite
// de deux façons différentes selon l'écran où on la regarde.

import { useSignaux } from "./hooks.js";

/* Au-delà, on ne dit plus « vu il y a » : un membre absent depuis un mois n'a
   pas une « dernière connexion », il a disparu. Annoncer « vu il y a 63 jours »
   ne renseigne personne et souligne l'abandon. */
const OUBLI = 30 * 864e5;

/**
 * Met la durée en mots.
 *
 * On arrondit vers le BAS et on reste vague au-delà de l'heure : « vu il y a
 * 2 h » se lit mieux que « vu il y a 2 h 47 », et personne n'a besoin de la
 * minute près pour décider s'il écrit.
 */
export function depuisQuand(instant) {
  const d = Date.now() - instant;
  if (d < 60e3) return "à l'instant";
  const min = Math.floor(d / 60e3);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j === 1) return "hier";
  if (j < 7) return `il y a ${j} jours`;
  const sem = Math.floor(j / 7);
  return sem === 1 ? "il y a une semaine" : `il y a ${sem} semaines`;
}

/**
 * L'état d'un membre, lu à l'instant.
 *
 * Rend `{ enLigne, vuA, texte }`. `texte` vaut `null` quand il n'y a rien
 * d'honnête à dire — présence jamais enregistrée, ou trop ancienne. Mieux vaut
 * ne rien écrire qu'écrire une approximation.
 */
export function usePresence(ctx, uid) {
  // Un simple compteur qui change quand la présence bouge : il force le rendu
  // sans qu'on ait à recopier l'état des signaux dans React.
  useSignaux(ctx?.signaux);

  if (!uid || !ctx?.signaux) return { enLigne: false, vuA: 0, texte: null };
  const s = ctx.signaux.instantane();
  if (s.enLigne?.has(uid)) return { enLigne: true, vuA: Date.now(), texte: "En ligne" };

  const vuA = s.vuA?.get(uid) || 0;
  if (!vuA || Date.now() - vuA > OUBLI) return { enLigne: false, vuA: 0, texte: null };
  return { enLigne: false, vuA, texte: `Vu ${depuisQuand(vuA)}` };
}

/**
 * La ligne d'état, telle qu'elle s'affiche.
 *
 * Le point vert n'est PAS un simple point : il porte aussi le mot « En ligne ».
 * Une pastille seule demande d'en connaître le code — et sur un petit écran,
 * un point de six pixels échappe à beaucoup de regards.
 */
export default function Presence({ ctx, uid, court = false }) {
  const { enLigne, texte } = usePresence(ctx, uid);
  if (!texte) return null;
  return (
    <span className={`presence${enLigne ? " est-en-ligne" : ""}`}>
      {enLigne && <span className="presence__point" aria-hidden="true" />}
      {court && enLigne ? "En ligne" : texte}
    </span>
  );
}
